<template>
  <div>
    <a-page-header title="诊断展示阶段管理">
      <template #extra>
        <a-space>
          <a-button type="primary" @click="handleBatchAddLogs">
            <template #icon><PlusOutlined /></template>
            批量添加日志
          </a-button>
          <a-button @click="handleViewLogs">
            <template #icon><UnorderedListOutlined /></template>
            查看日志
          </a-button>
          <a-button @click="handleViewReplay">
            <template #icon><PlayCircleOutlined /></template>
            会话回放
          </a-button>
          <a-button @click="handleManageComponents">
            <template #icon><SettingOutlined /></template>
            组件管理
          </a-button>
          <a-button type="primary" @click="handlePublishVersion">
            <template #icon><RocketOutlined /></template>
            文案版本发布
          </a-button>
        </a-space>
      </template>
    </a-page-header>

    <div style="display: flex; margin-top: 16px; gap: 16px; height: calc(100vh - 200px)">
      <!-- 左边列：一级阶段列表 -->
      <a-card style="flex: 0 0 300px; overflow-y: auto">
        <template #title>
          <div style="display: flex; justify-content: space-between; align-items: center">
            <span>一级阶段</span>
            <a-button type="primary" size="small" @click="handleCreateLevel1">
              <template #icon><PlusOutlined /></template>
            </a-button>
          </div>
        </template>
        <a-list
          :data-source="level1Stages"
          :loading="loading"
          size="small"
        >
          <template #renderItem="{ item }">
            <a-list-item
              :class="{ 'selected': selectedLevel1Id === item.id }"
              style="cursor: pointer; padding: 8px 12px"
              @click="handleSelectLevel1(item)"
            >
              <a-list-item-meta>
                <template #title>
                  <span>{{ item.stage_code }}  {{ item.stage_name }}</span>
                </template>
              </a-list-item-meta>
            </a-list-item>
          </template>
        </a-list>
      </a-card>

      <!-- 中间列：二级阶段列表 + 日志列表 -->
      <a-card style="flex: 1; overflow-y: auto">
        <template #title>
          <div style="display: flex; justify-content: space-between; align-items: center">
            <span>二级阶段</span>
            <a-button type="primary" size="small" @click="handleCreateLevel2" :disabled="!selectedLevel1Id">
              <template #icon><PlusOutlined /></template>
            </a-button>
          </div>
        </template>
        <div v-if="!selectedLevel1Id" style="text-align: center; padding: 40px; color: #999">
          请先选择一级阶段
        </div>
        <div v-else>
          <div
            v-for="stage2 in level2Stages"
            :key="stage2.id"
            style="margin-bottom: 8px"
          >
            <!-- 二级阶段行 -->
            <div
              :class="{ 'stage-row': true, 'expanded': expandedStage2Id === stage2.id }"
              style="display: flex; align-items: center; padding: 12px; border: 1px solid #d9d9d9; border-radius: 4px; cursor: pointer"
              @click="handleToggleStage2(stage2)"
            >
              <div style="flex: 1">
                <span>{{ stage2.stage_code }}  {{ stage2.stage_name }}</span>
              </div>
              <div style="display: flex; gap: 8px; align-items: center">
                <EditOutlined
                  style="cursor: pointer; color: #1890ff"
                  @click.stop="handleEditStage2(stage2)"
                />
                <DeleteOutlined
                  style="cursor: pointer; color: #ff4d4f"
                  @click.stop="handleDeleteStage2(stage2.id)"
                />
              </div>
            </div>
            
            <!-- 日志列表（展开时显示） -->
            <div
              v-if="expandedStage2Id === stage2.id"
              style="margin-left: 24px; margin-top: 8px; padding-left: 16px; border-left: 2px solid #1890ff"
            >
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px">
                <span style="font-weight: 500">日志列表</span>
                <a-button type="primary" size="small" @click="handleCreateLog(stage2.id)">
                  <template #icon><PlusOutlined /></template>
                  添加日志
                </a-button>
              </div>
              <div v-if="stage2Logs[stage2.id] && stage2Logs[stage2.id].length > 0" class="console-log">
                <div
                  v-for="log in stage2Logs[stage2.id]"
                  :key="log.id"
                  class="console-item"
                >
                  <div style="flex: 1">
                    <span v-if="log.tag_code" class="console-tag">[{{ log.tag_code }}]{{ getTagName(log.tag_code) }}</span>
                    <span :class="['console-text', `level-${log.log_level}`]">{{ formatLogTextForDisplay(log.log_text) }}</span>
                    <span 
                      v-if="hasStatusText(log) && getLogAnimationState(log.id).showEllipsis && !getLogAnimationState(log.id).showStatus" 
                      class="console-ellipsis"
                    >
                      <span class="ellipsis-dot">.</span><span class="ellipsis-dot">.</span><span class="ellipsis-dot">.</span>
                    </span>
                    <div v-if="hasStatusText(log) && getLogAnimationState(log.id).showStatus" class="console-status">
                      <span v-if="log.status_success_text" class="status-success">
                        ✅ {{ log.status_success_text }}
                      </span>
                      <span v-if="log.status_failed_text" class="status-error">
                        ❌ {{ log.status_failed_text }}
                      </span>
                      <span v-if="log.status_info_text" class="status-info">
                        ℹ️ {{ log.status_info_text }}
                      </span>
                      <span v-if="log.status_warning_text" class="status-warning">
                        ⚠️ {{ log.status_warning_text }}
                      </span>
                    </div>
                  </div>
                  <div class="console-actions">
                    <EditOutlined
                      style="cursor: pointer; color: #1890ff"
                      @click="handleEditLog(log)"
                    />
                    <DeleteOutlined
                      style="cursor: pointer; color: #ff4d4f"
                      @click="handleDeleteLog(log.id)"
                    />
                  </div>
                </div>
              </div>
              <div v-else style="text-align: center; padding: 20px; color: #999">
                暂无日志
              </div>
            </div>
          </div>
          <div v-if="level2Stages.length === 0" style="text-align: center; padding: 40px; color: #999">
            该一级阶段下暂无二级阶段
          </div>
        </div>
      </a-card>

      <!-- 右边列：二级阶段描述 -->
      <a-card style="flex: 0 0 300px; overflow-y: auto">
        <template #title>
          <span>阶段描述</span>
        </template>
        <div v-if="selectedStage2">
          <div style="margin-bottom: 12px">
            <strong>阶段代码：</strong>{{ selectedStage2.stage_code }}
          </div>
          <div style="margin-bottom: 12px">
            <strong>阶段名称：</strong>{{ selectedStage2.stage_name }}
          </div>
          <div>
            <strong>描述：</strong>
            <div style="margin-top: 8px; padding: 12px; background: #f5f5f5; border-radius: 4px; min-height: 100px">
              {{ selectedStage2.description || '暂无描述' }}
            </div>
          </div>
        </div>
        <div v-else style="text-align: center; padding: 40px; color: #999">
          请选择二级阶段查看描述
        </div>
      </a-card>
    </div>

    <!-- 阶段编辑弹窗 -->
    <a-modal
      v-model:open="stageModalVisible"
      :title="stageModalTitle"
      @ok="handleStageSubmit"
      @cancel="handleStageCancel"
    >
      <a-form :model="stageFormData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="阶段名称" required>
          <a-input v-model:value="stageFormData.stage_name" placeholder="请输入阶段名称" />
        </a-form-item>
        <a-form-item label="阶段代码" required>
          <a-input v-model:value="stageFormData.stage_code" placeholder="请输入阶段代码" />
        </a-form-item>
        <a-form-item label="阶段顺序" required>
          <a-input-number
            v-model:value="stageFormData.stage_order"
            :min="1"
            placeholder="请输入阶段顺序"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea v-model:value="stageFormData.description" :rows="3" placeholder="请输入描述" />
        </a-form-item>
        <a-form-item label="状态">
          <a-radio-group v-model:value="stageFormData.status">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">禁用</a-radio>
          </a-radio-group>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 日志编辑弹窗 -->
    <a-modal
      v-model:open="logModalVisible"
      :title="logModalTitle"
      width="800px"
      @ok="handleLogSubmit"
      @cancel="handleLogCancel"
    >
      <a-form :model="logFormData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="日志标签" required>
          <a-select
            v-model:value="logFormData.tag_code"
            placeholder="请选择日志标签"
            show-search
            :filter-option="filterTagOption"
          >
            <a-select-option
              v-for="tag in logTags"
              :key="tag.tag_code"
              :value="tag.tag_code"
            >
              [{{ tag.tag_code }}] {{ tag.tag_name }}
            </a-select-option>
          </a-select>
          <div style="color: #999; font-size: 12px; margin-top: 4px" v-if="logFormData.tag_code">
            {{ getTagDescription(logFormData.tag_code) }}
          </div>
        </a-form-item>
        <a-form-item label="日志文本" required>
          <a-textarea
            ref="logTextTextarea"
            v-model:value="logFormData.log_text"
            :rows="4"
            placeholder="请输入日志文本（支持变量占位符，如 {random_number}、{timestamp}、{fault_phenomenon} 等）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px; margin-bottom: 8px">
            支持变量：{random_number}、{random_float}、{timestamp}、{task_id}、{user_id}、{step_id}、{worker_id}、{fault_phenomenon}
            <br>
            支持组件占位符：{component:component_code}
            <br>
            注意：标签会自动合并到日志文本前，格式为 [标签] 日志文本
          </div>
          <div style="margin-top: 8px">
            <span style="color: #999; font-size: 12px; margin-right: 8px">快速插入组件：</span>
            <a-select
              v-model:value="selectedLogComponent"
              placeholder="选择组件插入占位符"
              style="width: 300px"
              show-search
              :filter-option="filterComponentOption"
              @focus="saveLogTextareaCursor"
              @change="handleLogInsertComponent"
            >
              <a-select-option
                v-for="component in componentList"
                :key="component.component_code"
                :value="component.component_code"
              >
                [{{ component.component_series }}] {{ component.component_name }} {{ component.version || '' }}
              </a-select-option>
            </a-select>
            <span style="color: #999; font-size: 12px; margin-left: 8px">
              格式：{component:component_code}
            </span>
          </div>
        </a-form-item>
        <a-form-item label="成功反馈">
          <a-textarea
            v-model:value="logFormData.status_success_text"
            :rows="2"
            placeholder="请输入成功时的反馈文本（如：初始化完成，支持变量占位符）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            成功时会自动添加 ✅ 图标，格式为：日志文本 ✅ 反馈文本
          </div>
        </a-form-item>
        <a-form-item label="失败反馈">
          <a-textarea
            v-model:value="logFormData.status_failed_text"
            :rows="2"
            placeholder="请输入失败时的反馈文本（如：初始化失败，支持变量占位符）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            失败时会自动添加 ❌ 图标，格式为：日志文本 ❌ 反馈文本
          </div>
        </a-form-item>
        <a-form-item label="信息反馈">
          <a-textarea
            v-model:value="logFormData.status_info_text"
            :rows="2"
            placeholder="请输入信息/提醒时的反馈文本（如：初始化完成，但连接出现断帧，可能不稳定，支持变量占位符）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            信息时会自动添加 ℹ️ 图标，格式为：日志文本 ℹ️ 反馈文本
          </div>
        </a-form-item>
        <a-form-item label="告警反馈">
          <a-textarea
            v-model:value="logFormData.status_warning_text"
            :rows="2"
            placeholder="请输入告警时的反馈文本（支持变量占位符）"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            告警时会自动添加 ⚠️ 图标，格式为：日志文本 ⚠️ 反馈文本
          </div>
        </a-form-item>
        <a-form-item label="日志级别">
          <a-select v-model:value="logFormData.log_level">
            <a-select-option value="info">info</a-select-option>
            <a-select-option value="warning">warning</a-select-option>
            <a-select-option value="error">error</a-select-option>
            <a-select-option value="success">success</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="显示顺序">
          <a-input-number
            v-model:value="logFormData.display_order"
            :min="0"
            placeholder="请输入显示顺序"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="延迟毫秒">
          <a-input-number
            v-model:value="logFormData.delay_ms"
            :min="0"
            placeholder="请输入延迟毫秒数"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="等待秒数">
          <a-input-number
            v-model:value="logFormData.wait_seconds"
            :min="0"
            :step="0.1"
            placeholder="请输入等待秒数"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="演示模式">
          <a-switch v-model:checked="logFormData.is_demo" :checked-value="1" :unchecked-value="0" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 批量添加日志弹窗 -->
    <a-modal
      v-model:open="batchAddLogsModalVisible"
      title="批量添加日志"
      width="900px"
      @ok="handleBatchAddLogsSubmit"
      @cancel="handleBatchAddLogsCancel"
    >
      <a-form :model="batchAddLogsFormData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="选择阶段" required>
          <a-select
            v-model:value="batchAddLogsFormData.stage_id"
            placeholder="请选择要添加日志的阶段"
            show-search
            :filter-option="filterStageOption"
            style="width: 100%"
          >
            <a-select-option
              v-for="stage in allStagesList"
              :key="stage.id"
              :value="stage.id"
            >
              {{ stage.stage_code }} - {{ stage.stage_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="日志文本" required>
          <a-textarea
            ref="batchLogTextTextarea"
            v-model:value="batchAddLogsFormData.log_text"
            :rows="10"
            placeholder="请输入日志文本，每行一条。支持格式：&#10;[标签] 日志文本&#10;[标签] 日志文本 ✅ 状态反馈&#10;示例：&#10;[LOAD] 加载 {component:semantic_parser}...&#10;[CONNECT] 连接 {component:vector_db}...&#10;[LOADING] 同步 {component:case_library} 案例索引..."
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px; margin-bottom: 8px">
            每行一条日志，支持格式：[标签] 日志文本 [✅/❌/ℹ️/⚠️] 状态反馈。支持组件占位符：{component:component_code}
          </div>
          <div style="margin-top: 8px">
            <span style="color: #999; font-size: 12px; margin-right: 8px">快速插入组件：</span>
            <a-select
              v-model:value="selectedBatchComponent"
              placeholder="选择组件插入占位符"
              style="width: 300px"
              show-search
              :filter-option="filterComponentOption"
              @focus="saveBatchTextareaCursor"
              @change="handleBatchInsertComponent"
            >
              <a-select-option
                v-for="component in componentList"
                :key="component.component_code"
                :value="component.component_code"
              >
                [{{ component.component_series }}] {{ component.component_name }} {{ component.version || '' }}
              </a-select-option>
            </a-select>
            <span style="color: #999; font-size: 12px; margin-left: 8px">
              格式：{component:component_code}
            </span>
          </div>
        </a-form-item>
        <a-form-item label="日志级别">
          <a-select v-model:value="batchAddLogsFormData.log_level" style="width: 100%">
            <a-select-option value="info">info</a-select-option>
            <a-select-option value="warning">warning</a-select-option>
            <a-select-option value="error">error</a-select-option>
            <a-select-option value="success">success</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="解析预览" v-if="parsedLogs.length > 0">
          <div style="max-height: 300px; overflow-y: auto; padding: 12px; background: #f5f5f5; border-radius: 4px">
            <div
              v-for="(log, index) in parsedLogs"
              :key="index"
              style="margin-bottom: 8px; padding: 8px; background: white; border-radius: 4px"
            >
              <div style="font-weight: 500; margin-bottom: 4px">日志 {{ index + 1 }}:</div>
              <div>
                <a-tag v-if="log.tag_code" color="blue" style="margin-right: 8px">
                  [{{ log.tag_code }}]
                </a-tag>
                <span v-html="formatLogTextWithComponents(log.log_text)"></span>
                <span v-if="log.status_success_text" style="color: #52c41a; margin-left: 8px">
                  ✅ {{ log.status_success_text }}
                </span>
                <span v-if="log.status_failed_text" style="color: #ff4d4f; margin-left: 8px">
                  ❌ {{ log.status_failed_text }}
                </span>
                <span v-if="log.status_info_text" style="color: #1890ff; margin-left: 8px">
                  ℹ️ {{ log.status_info_text }}
                </span>
                <span v-if="log.status_warning_text" style="color: #faad14; margin-left: 8px">
                  ⚠️ {{ log.status_warning_text }}
                </span>
              </div>
              <!-- 显示组件信息 -->
              <div v-if="log.components && log.components.length > 0" style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e8e8e8">
                <div style="font-size: 12px; color: #666; margin-bottom: 4px">组件信息：</div>
                <div v-for="component in log.components" :key="component.component_code" style="margin-top: 4px">
                  <a-tag color="purple" style="margin-right: 4px">
                    {{ component.component_code }}
                  </a-tag>
                  <span style="font-size: 12px">
                    [{{ component.component_series }}]{{ component.component_name }}
                    <span v-if="component.version"> {{ component.version }}</span>
                    <span v-if="component.data_volume"> {{ component.data_volume }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 文案版本发布弹窗 -->
    <a-modal
      v-model:open="versionPublishModalVisible"
      title="发布文案新版本"
      width="700px"
      @ok="handleVersionPublishSubmit"
      @cancel="handleVersionPublishCancel"
    >
      <a-form :model="versionPublishFormData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <!-- 当前版本信息 -->
        <a-form-item label="当前版本">
          <div style="padding: 12px; background: #f0f7ff; border-radius: 4px; border: 1px solid #91d5ff">
            <div style="margin-bottom: 8px">
              <span style="font-weight: 500; color: #1890ff; font-size: 16px">{{ currentVersion || '加载中...' }}</span>
            </div>
            <div style="margin-bottom: 4px; color: #666; font-size: 13px">
              <span style="color: #999">更新时间：</span>{{ currentVersionTime || '加载中...' }}
            </div>
            <div style="color: #666; font-size: 13px">
              <span style="color: #999">版本说明：</span>{{ currentVersionDescription || '无' }}
            </div>
          </div>
        </a-form-item>
        
        <!-- 待发布版本和版本说明 -->
        <a-form-item label="待发布版本">
          <div style="padding: 12px; background: #f6ffed; border-radius: 4px; border: 1px solid #b7eb8f; margin-bottom: 12px">
            <span style="font-weight: 500; color: #52c41a; font-size: 16px">{{ nextVersion || '计算中...' }}</span>
          </div>
          <a-textarea
            v-model:value="versionPublishFormData.description"
            :rows="3"
            placeholder="请输入版本说明（可选），例如：更新了故障诊断相关文案、新增了组件信息等"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">
            建议填写本次版本的主要变更内容
          </div>
        </a-form-item>
        
        <!-- 检查项 -->
        <a-form-item label="检查项">
          <a-alert
            type="info"
            show-icon
          >
            <template #message>
              <div style="white-space: pre-line; line-height: 1.8">
                <div style="font-weight: 500; margin-bottom: 8px">请检查以下内容是否有变更：</div>
                <div style="margin-left: 16px">
                  <div style="margin-bottom: 4px">• 日志项</div>
                  <div style="margin-left: 16px; color: #666; margin-bottom: 8px">诊断展示阶段中的日志文本、标签、状态反馈等</div>
                  <div style="margin-bottom: 4px">• 组件信息</div>
                  <div style="margin-left: 16px; color: #666; margin-bottom: 8px">组件管理中的组件版本、数据量、更新说明等</div>
                  <div style="margin-bottom: 4px">• 标签信息</div>
                  <div style="margin-left: 16px; color: #666">日志标签的名称、描述等</div>
                </div>
              </div>
            </template>
          </a-alert>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { PlusOutlined, UnorderedListOutlined, PlayCircleOutlined, EditOutlined, DeleteOutlined, SettingOutlined, RocketOutlined } from '@ant-design/icons-vue';
import request from '@/api/request';

export default {
  name: 'DiagnosisDisplayStage',
  components: {
    PlusOutlined,
    UnorderedListOutlined,
    PlayCircleOutlined,
    SettingOutlined,
    EditOutlined,
    DeleteOutlined,
    RocketOutlined
  },
  data() {
    return {
      loading: false,
      // 一级阶段列表
      level1Stages: [],
      selectedLevel1Id: null,
      // 二级阶段列表
      level2Stages: [],
      expandedStage2Id: null,
      selectedStage2: null,
      // 日志数据：key为stage2的id，value为日志数组
      stage2Logs: {},
      // 日志动画状态：key为log.id，value为{ showEllipsis: boolean, showStatus: boolean }
      logAnimationStates: {},
      // 标签列表
      logTags: [],
      // 阶段表单
      stageModalVisible: false,
      stageModalTitle: '',
      stageFormData: {
        stage_name: '',
        stage_code: '',
        stage_order: 1,
        level: 1,
        parent_id: null,
        description: '',
        status: 1
      },
      // 日志表单
      logModalVisible: false,
      logModalTitle: '',
      logFormData: {
        stage_id: null,
        log_text: '',
        status_success_text: '',
        status_failed_text: '',
        status_info_text: '',
        status_warning_text: '',
        log_level: 'info',
        tag_code: null,
        display_order: 0,
        delay_ms: 0,
        wait_seconds: null,
        is_demo: 0
      },
      // 批量添加日志
      batchAddLogsModalVisible: false,
      batchAddLogsFormData: {
        stage_id: null,
        log_text: '',
        log_level: 'info'
      },
      parsedLogs: [],
      // 所有阶段列表（用于批量添加日志）
      allStagesList: [],
      // 组件列表（用于批量添加日志）
      componentList: [],
      // 组件映射（component_code -> component）
      componentMap: {},
      // 批量添加日志时选中的组件
      selectedBatchComponent: undefined,
      // 单个日志添加时选中的组件
      selectedLogComponent: undefined,
      // 保存的光标位置
      savedLogTextareaCursor: 0,
      savedBatchTextareaCursor: 0,
      // 文案版本发布
      versionPublishModalVisible: false,
      currentVersion: '',
      currentVersionTime: '',
      currentVersionDescription: '',
      nextVersion: '',
      versionPublishFormData: {
        description: ''
      }
    };
  },
  watch: {
    // 监听日志文本变化，实时解析
    'batchAddLogsFormData.log_text'(newVal) {
      if (newVal) {
        this.parsedLogs = this.parseLogText(newVal);
      } else {
        this.parsedLogs = [];
      }
    }
  },
  mounted() {
    this.fetchLevel1Stages();
    this.fetchLogTags();
    this.fetchComponents();
  },
  methods: {
    // 获取一级阶段列表
    async fetchLevel1Stages() {
      this.loading = true;
      try {
        const response = await request.get('/diagnosis-display/stages', {
          params: {
            level: 1,
            pageSize: 1000
          }
        });
        
        if (response.code === 200) {
          this.level1Stages = (response.data.items || []).sort((a, b) => a.stage_order - b.stage_order);
          
          // 默认选中第一个一级阶段
          if (this.level1Stages.length > 0 && !this.selectedLevel1Id) {
            this.handleSelectLevel1(this.level1Stages[0]);
          }
        }
      } catch (error) {
        this.$message.error('获取一级阶段失败: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    // 选择一级阶段
    async handleSelectLevel1(stage1) {
      this.selectedLevel1Id = stage1.id;
      this.expandedStage2Id = null;
      this.selectedStage2 = null;
      await this.fetchLevel2Stages(stage1.id);
    },
    
    // 获取二级阶段列表
    async fetchLevel2Stages(parentId) {
      this.loading = true;
      try {
        const response = await request.get('/diagnosis-display/stages', {
          params: {
            level: 2,
            parent_id: parentId,
            pageSize: 1000
          }
        });
        
        if (response.code === 200) {
          this.level2Stages = (response.data.items || []).sort((a, b) => a.stage_order - b.stage_order);
          // 清空之前的日志数据
          this.stage2Logs = {};
        }
      } catch (error) {
        this.$message.error('获取二级阶段失败: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    // 切换二级阶段展开/收起
    async handleToggleStage2(stage2) {
      if (this.expandedStage2Id === stage2.id) {
        // 收起
        this.expandedStage2Id = null;
        this.selectedStage2 = null;
      } else {
        // 展开
        this.expandedStage2Id = stage2.id;
        this.selectedStage2 = stage2;
        // 如果还没有加载该二级阶段的日志，则加载
        if (!this.stage2Logs[stage2.id]) {
          await this.fetchLogs(stage2.id);
        } else {
          // 如果日志已存在，重新启动动画
          this.$nextTick(() => {
            const logs = this.stage2Logs[stage2.id];
            logs.forEach(log => {
              if (this.hasStatusText(log)) {
                // 重置状态
                this.logAnimationStates[log.id] = {
                  showEllipsis: false,
                  showStatus: false
                };
                // 启动动画
                this.startLogAnimation(log.id);
              }
            });
          });
        }
      }
    },
    
    // 获取日志列表
    async fetchLogs(stageId) {
      try {
        // 确保组件列表已加载（用于替换组件占位符）
        if (this.componentList.length === 0) {
          await this.fetchComponents();
        }
        
        const response = await request.get('/diagnosis-display/log-items', {
          params: {
            stage_id: stageId,
            pageSize: 1000
          }
        });
        
        if (response.code === 200) {
          const logs = (response.data.items || []).sort((a, b) => a.display_order - b.display_order);
          
          // 清理旧的动画状态（只保留当前日志的）
          const logIds = new Set(logs.map(log => log.id));
          Object.keys(this.logAnimationStates).forEach(id => {
            if (!logIds.has(parseInt(id))) {
              delete this.logAnimationStates[id];
            }
          });
          
          this.stage2Logs = { ...this.stage2Logs, [stageId]: logs };
          
          // 为有状态文本的日志项启动动画
          this.$nextTick(() => {
            logs.forEach(log => {
              if (this.hasStatusText(log)) {
                // 重置状态
                this.logAnimationStates[log.id] = {
                  showEllipsis: false,
                  showStatus: false
                };
                // 启动动画
                this.startLogAnimation(log.id);
              }
            });
          });
        }
      } catch (error) {
        this.$message.error('获取日志失败: ' + error.message);
      }
    },
    
    // 创建一级阶段
    handleCreateLevel1() {
      this.stageModalTitle = '新建一级阶段';
      this.stageFormData = {
        stage_name: '',
        stage_code: '',
        stage_order: this.level1Stages.length > 0 ? Math.max(...this.level1Stages.map(s => s.stage_order)) + 1 : 1,
        level: 1,
        parent_id: null,
        description: '',
        status: 1
      };
      this.stageModalVisible = true;
    },
    
    // 创建二级阶段
    handleCreateLevel2() {
      this.stageModalTitle = '新建二级阶段';
      this.stageFormData = {
        stage_name: '',
        stage_code: '',
        stage_order: this.level2Stages.length > 0 ? Math.max(...this.level2Stages.map(s => s.stage_order)) + 1 : 1,
        level: 2,
        parent_id: this.selectedLevel1Id,
        description: '',
        status: 1
      };
      this.stageModalVisible = true;
    },
    
    // 编辑二级阶段
    handleEditStage2(stage2) {
      this.stageModalTitle = '编辑二级阶段';
      this.stageFormData = { ...stage2 };
      this.stageModalVisible = true;
    },
    
    // 删除二级阶段
    async handleDeleteStage2(id) {
      try {
        const response = await request.delete(`/diagnosis-display/stages/${id}`);
        if (response.code === 200) {
          this.$message.success('删除成功');
          await this.fetchLevel2Stages(this.selectedLevel1Id);
          if (this.expandedStage2Id === id) {
            this.expandedStage2Id = null;
            this.selectedStage2 = null;
          }
        }
      } catch (error) {
        this.$message.error('删除失败: ' + error.message);
      }
    },
    
    // 阶段表单提交
    async handleStageSubmit() {
      try {
        if (this.stageFormData.id) {
          // 编辑
          const response = await request.put(`/diagnosis-display/stages/${this.stageFormData.id}`, this.stageFormData);
          if (response.code === 200) {
            this.$message.success('更新成功');
            this.stageModalVisible = false;
            if (this.stageFormData.level === 1) {
              await this.fetchLevel1Stages();
            } else {
              await this.fetchLevel2Stages(this.selectedLevel1Id);
            }
          }
        } else {
          // 新建
          const response = await request.post('/diagnosis-display/stages', this.stageFormData);
          if (response.code === 200) {
            this.$message.success('创建成功');
            this.stageModalVisible = false;
            if (this.stageFormData.level === 1) {
              await this.fetchLevel1Stages();
            } else {
              await this.fetchLevel2Stages(this.selectedLevel1Id);
            }
          }
        }
      } catch (error) {
        this.$message.error('操作失败: ' + error.message);
      }
    },
    
    // 阶段表单取消
    handleStageCancel() {
      this.stageModalVisible = false;
      this.stageFormData = {
        stage_name: '',
        stage_code: '',
        stage_order: 1,
        level: 1,
        parent_id: null,
        description: '',
        status: 1
      };
    },
    
    // 获取标签列表
    async fetchLogTags() {
      try {
        const response = await request.get('/diagnosis-display/tags', {
          params: { status: 1 }
        });
        if (response.code === 200) {
          this.logTags = response.data || [];
        }
      } catch (error) {
        console.error('获取标签列表失败:', error);
      }
    },
    
    // 获取组件列表
    async fetchComponents() {
      try {
        const response = await request.get('/diagnosis-display/components', {
          params: { status: 1 }
        });
        if (response.code === 200) {
          this.componentList = response.data || [];
          // 建立组件映射
          this.componentMap = {};
          this.componentList.forEach(component => {
            this.componentMap[component.component_code] = component;
          });
        }
      } catch (error) {
        console.error('获取组件列表失败:', error);
      }
    },
    
    // 组件选择器过滤
    filterComponentOption(input, option) {
      const component = this.componentList.find(c => c.component_code === option.value);
      if (!component) return false;
      const searchText = input.toLowerCase();
      return (
        component.component_code.toLowerCase().includes(searchText) ||
        component.component_series.toLowerCase().includes(searchText) ||
        component.component_name.toLowerCase().includes(searchText) ||
        (component.version && component.version.toLowerCase().includes(searchText))
      );
    },
    
    // 保存批量添加日志文本域的光标位置
    saveBatchTextareaCursor() {
      const textareaRef = this.$refs.batchLogTextTextarea;
      if (textareaRef) {
        let textarea = null;
        if (textareaRef.resizableTextArea && textareaRef.resizableTextArea.textArea) {
          textarea = textareaRef.resizableTextArea.textArea;
        } else if (textareaRef.$el) {
          textarea = textareaRef.$el.querySelector('textarea');
        }
        
        if (textarea) {
          this.savedBatchTextareaCursor = textarea.selectionStart || textarea.selectionStart || (this.batchAddLogsFormData.log_text || '').length;
        }
      }
    },
    
    // 批量添加日志时插入组件占位符
    handleBatchInsertComponent(componentCode) {
      if (!componentCode) return;
      
      const placeholder = `{component:${componentCode}}`;
      const currentText = this.batchAddLogsFormData.log_text || '';
      
      // 使用保存的光标位置，如果没有则使用文本末尾
      const cursorPos = this.savedBatchTextareaCursor >= 0 ? this.savedBatchTextareaCursor : currentText.length;
      
      // 在光标位置插入占位符
      const newText = currentText.slice(0, cursorPos) + placeholder + currentText.slice(cursorPos);
      this.batchAddLogsFormData.log_text = newText;
      
      // 重置选择器和光标位置
      this.selectedBatchComponent = undefined;
      this.savedBatchTextareaCursor = cursorPos + placeholder.length;
      
      // 聚焦到文本域并设置光标位置
      this.$nextTick(() => {
        const textareaRef = this.$refs.batchLogTextTextarea;
        if (textareaRef) {
          let textarea = null;
          if (textareaRef.resizableTextArea && textareaRef.resizableTextArea.textArea) {
            textarea = textareaRef.resizableTextArea.textArea;
          } else if (textareaRef.$el) {
            textarea = textareaRef.$el.querySelector('textarea');
          }
          
          if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(this.savedBatchTextareaCursor, this.savedBatchTextareaCursor);
          }
        }
      });
    },
    
    // 保存单个日志文本域的光标位置
    saveLogTextareaCursor() {
      const textareaRef = this.$refs.logTextTextarea;
      if (textareaRef) {
        let textarea = null;
        if (textareaRef.resizableTextArea && textareaRef.resizableTextArea.textArea) {
          textarea = textareaRef.resizableTextArea.textArea;
        } else if (textareaRef.$el) {
          textarea = textareaRef.$el.querySelector('textarea');
        }
        
        if (textarea) {
          this.savedLogTextareaCursor = textarea.selectionStart || textarea.selectionStart || (this.logFormData.log_text || '').length;
        }
      }
    },
    
    // 单个日志添加时插入组件占位符
    handleLogInsertComponent(componentCode) {
      if (!componentCode) return;
      
      const placeholder = `{component:${componentCode}}`;
      const currentText = this.logFormData.log_text || '';
      
      // 使用保存的光标位置，如果没有则使用文本末尾
      const cursorPos = this.savedLogTextareaCursor >= 0 ? this.savedLogTextareaCursor : currentText.length;
      
      // 在光标位置插入占位符
      const newText = currentText.slice(0, cursorPos) + placeholder + currentText.slice(cursorPos);
      this.logFormData.log_text = newText;
      
      // 重置选择器和光标位置
      this.selectedLogComponent = undefined;
      this.savedLogTextareaCursor = cursorPos + placeholder.length;
      
      // 聚焦到文本域并设置光标位置
      this.$nextTick(() => {
        const textareaRef = this.$refs.logTextTextarea;
        if (textareaRef) {
          let textarea = null;
          if (textareaRef.resizableTextArea && textareaRef.resizableTextArea.textArea) {
            textarea = textareaRef.resizableTextArea.textArea;
          } else if (textareaRef.$el) {
            textarea = textareaRef.$el.querySelector('textarea');
          }
          
          if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(this.savedLogTextareaCursor, this.savedLogTextareaCursor);
          }
        }
      });
    },
    
    // 格式化日志文本，高亮显示组件占位符（用于预览）
    formatLogTextWithComponents(text) {
      if (!text) return '';
      
      // 先转义 HTML 特殊字符
      let escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      
      // 匹配组件占位符 {component:component_code} 或 {component:component_code:mode}
      const componentPattern = /\{component:([\w_]+)(?::(\w+))?\}/g;
      let result = escaped;
      
      // 使用 replace 方法，更简洁
      result = result.replace(componentPattern, (match, componentCode) => {
        const component = this.componentMap[componentCode];
        if (component) {
          // 高亮显示组件占位符
          return `<span style="background: #e6f7ff; color: #1890ff; padding: 2px 4px; border-radius: 2px; font-weight: 500;">${match}</span>`;
        }
        return match;
      });
      
      return result;
    },
    
    // 格式化日志文本，替换组件占位符为实际组件信息（用于显示）
    formatLogTextForDisplay(text) {
      if (!text) return '';
      
      // 匹配组件占位符 {component:component_code} 或 {component:component_code:mode}
      const componentPattern = /\{component:([\w_]+)(?::(\w+))?\}/g;
      let result = text;
      
      // 使用 replace 方法替换组件占位符
      result = result.replace(componentPattern, (match, componentCode, mode) => {
        const component = this.componentMap[componentCode];
        if (component) {
          // 根据模式返回不同格式
          const displayMode = mode || 'full';
          return this.formatComponentForDisplay(component, displayMode);
        }
        // 如果组件不存在，保持原样
        return match;
      });
      
      return result;
    },
    
    // 格式化组件显示文本（用于实际显示）
    formatComponentForDisplay(component, mode = 'full') {
      const { component_series, component_name, version, data_volume, changelog } = component;
      
      // 根据模式返回不同格式
      switch (mode) {
        case 'name':
          return component_name || '';
        case 'version':
          return version || '';
        case 'data':
          return data_volume || '';
        case 'full':
        default:
          // 默认格式：[{series}]{name} {version} {data_volume} {changelog}
          let parts = [];
          
          // 系列和名称
          if (component_series && component_name) {
            parts.push(`[${component_series}]${component_name}`);
          } else if (component_name) {
            parts.push(component_name);
          }
          
          // 版本号
          if (version) {
            parts.push(version);
          }
          
          // 数据量
          if (data_volume) {
            parts.push(data_volume);
          }
          
          // 更新说明
          if (changelog) {
            parts.push(changelog);
          }
          
          return parts.join(' ');
      }
    },
    
    // 获取标签描述
    getTagDescription(tagCode) {
      const tag = this.logTags.find(t => t.tag_code === tagCode);
      return tag ? tag.description || tag.usage_scenario || '' : '';
    },
    
    // 获取标签名称
    getTagName(tagCode) {
      if (!tagCode) return '';
      const tag = this.logTags.find(t => t.tag_code === tagCode);
      return tag ? tag.tag_name : '';
    },
    
    // 标签选择器过滤
    filterTagOption(input, option) {
      const tag = this.logTags.find(t => t.tag_code === option.value);
      if (!tag) return false;
      const searchText = input.toLowerCase();
      return tag.tag_code.toLowerCase().includes(searchText) ||
             tag.tag_name.toLowerCase().includes(searchText) ||
             (tag.description && tag.description.toLowerCase().includes(searchText));
    },
    
    // 创建日志
    async handleCreateLog(stageId) {
      // 确保组件列表已加载
      if (this.componentList.length === 0) {
        await this.fetchComponents();
      }
      this.logModalTitle = '新建日志项';
      this.logFormData = {
        stage_id: stageId,
        log_text: '',
        status_success_text: '',
        status_failed_text: '',
        status_info_text: '',
        status_warning_text: '',
        log_level: 'info',
        tag_code: null,
        display_order: this.stage2Logs[stageId] ? this.stage2Logs[stageId].length : 0,
        delay_ms: 0,
        wait_seconds: null,
        is_demo: 0
      };
      this.selectedLogComponent = undefined;
      this.logModalVisible = true;
    },
    
    async handleEditLog(log) {
      // 确保组件列表已加载
      if (this.componentList.length === 0) {
        await this.fetchComponents();
      }
      this.logModalTitle = '编辑日志项';
      this.logFormData = { ...log };
      this.selectedLogComponent = undefined;
      this.savedLogTextareaCursor = 0;
      this.logModalVisible = true;
    },
    
    // 删除日志
    async handleDeleteLog(id) {
      try {
        const response = await request.delete(`/diagnosis-display/log-items/${id}`);
        if (response.code === 200) {
          this.$message.success('删除成功');
          // 清理动画状态
          delete this.logAnimationStates[id];
          // 重新加载当前展开的二级阶段的日志
          if (this.expandedStage2Id) {
            await this.fetchLogs(this.expandedStage2Id);
          }
        }
      } catch (error) {
        this.$message.error('删除失败: ' + error.message);
      }
    },
    
    // 日志表单提交
    async handleLogSubmit() {
      try {
        if (this.logFormData.id) {
          // 编辑
          const response = await request.put(`/diagnosis-display/log-items/${this.logFormData.id}`, this.logFormData);
          if (response.code === 200) {
            this.$message.success('更新成功');
            this.logModalVisible = false;
            await this.fetchLogs(this.logFormData.stage_id);
          }
        } else {
          // 新建
          const response = await request.post('/diagnosis-display/log-items', this.logFormData);
          if (response.code === 200) {
            this.$message.success('创建成功');
            this.logModalVisible = false;
            await this.fetchLogs(this.logFormData.stage_id);
          }
        }
      } catch (error) {
        this.$message.error('操作失败: ' + error.message);
      }
    },
    
    // 日志表单取消
    handleLogCancel() {
      this.logModalVisible = false;
      this.logFormData = {
        stage_id: null,
        log_text: '',
        status_success_text: '',
        status_failed_text: '',
        status_info_text: '',
        status_warning_text: '',
        log_level: 'info',
        tag_code: null,
        display_order: 0,
        delay_ms: 0,
        wait_seconds: null,
        is_demo: 0
      };
      this.selectedLogComponent = undefined;
      this.savedLogTextareaCursor = 0;
    },
    
    // 获取日志级别颜色
    getLogLevelColor(level) {
      const colorMap = {
        'info': 'blue',
        'warning': 'orange',
        'error': 'red',
        'success': 'green'
      };
      return colorMap[level] || 'default';
    },
    
    // 检查是否有状态反馈文本
    hasStatusText(log) {
      return log.status_success_text || log.status_failed_text || log.status_info_text || log.status_warning_text;
    },
    
    // 获取日志动画状态
    getLogAnimationState(logId) {
      if (!this.logAnimationStates[logId]) {
        this.logAnimationStates[logId] = {
          showEllipsis: false,
          showStatus: false
        };
      }
      return this.logAnimationStates[logId];
    },
    
    // 启动日志动画
    startLogAnimation(logId) {
      const state = this.getLogAnimationState(logId);
      
      // 立即显示省略号
      state.showEllipsis = true;
      
      // 1秒后显示状态内容并隐藏省略号
      setTimeout(() => {
        state.showStatus = true;
        state.showEllipsis = false;
      }, 1000);
    },
    
    handleViewLogs() {
      this.$router.push('/diagnosis-log/log-items');
    },
    
    handleViewReplay() {
      this.$router.push('/diagnosis-log/replay');
    },
    
    handleManageComponents() {
      this.$router.push('/diagnosis-log/components');
    },
    
    // 计算下一个版本号
    calculateNextVersion(currentVersion) {
      if (!currentVersion) return '';
      
      const now = new Date();
      const dateStr = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0');
      
      // 如果当前版本是今天的，序号+1
      if (currentVersion.startsWith(dateStr)) {
        const currentSeq = parseInt(currentVersion.substring(8), 10);
        const nextSeq = currentSeq + 1;
        return dateStr + String(nextSeq).padStart(3, '0');
      } else {
        // 如果是新的一天，从001开始
        return dateStr + '001';
      }
    },
    
    // 格式化日期时间
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return '';
      const date = new Date(dateTimeStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    
    // 打开文案版本发布弹窗
    async handlePublishVersion() {
      try {
        // 获取当前版本号
        const res = await request.get('/ssz_ai/display-texts/version');
        if (res.code === 200) {
          this.currentVersion = res.data.version;
          
          // 获取版本历史，找到当前版本的详细信息
          const versionRes = await request.get('/ssz_ai/display-texts/versions?page=1&pageSize=1');
          if (versionRes.code === 200 && versionRes.data.list.length > 0) {
            const currentVersionInfo = versionRes.data.list[0];
            this.currentVersionTime = this.formatDateTime(currentVersionInfo.create_time);
            this.currentVersionDescription = currentVersionInfo.description || '无';
          } else {
            this.currentVersionTime = '';
            this.currentVersionDescription = '无';
          }
          
          // 计算待发布版本号
          this.nextVersion = this.calculateNextVersion(res.data.version);
        }
      } catch (error) {
        console.error('获取当前版本失败:', error);
        this.$message.error('获取当前版本失败');
        return;
      }
      this.versionPublishModalVisible = true;
      this.versionPublishFormData = {
        description: ''
      };
    },
    
    // 提交版本发布
    async handleVersionPublishSubmit() {
      try {
        const res = await request.post('/ssz_ai/display-texts/version', {
          description: this.versionPublishFormData.description || null
        });
        if (res.code === 200) {
          this.$message.success(`版本发布成功！新版本号：${res.data.version}`);
          this.currentVersion = res.data.version;
          this.currentVersionTime = '';
          this.currentVersionDescription = '';
          this.nextVersion = '';
          this.versionPublishModalVisible = false;
          this.versionPublishFormData = {
            description: ''
          };
        } else {
          this.$message.error(res.message || '版本发布失败');
        }
      } catch (error) {
        console.error('版本发布失败:', error);
        this.$message.error('版本发布失败');
      }
    },
    
    // 取消版本发布
    handleVersionPublishCancel() {
      this.versionPublishModalVisible = false;
      this.currentVersionTime = '';
      this.currentVersionDescription = '';
      this.nextVersion = '';
      this.versionPublishFormData = {
        description: ''
      };
    },
    
    // 打开批量添加日志弹窗
    async handleBatchAddLogs() {
      // 先获取所有阶段和组件列表
      await Promise.all([
        this.fetchAllStages(),
        this.fetchComponents()
      ]);
      this.batchAddLogsModalVisible = true;
      this.batchAddLogsFormData = {
        stage_id: null,
        log_text: '',
        log_level: 'info'
      };
      this.parsedLogs = [];
      this.selectedBatchComponent = undefined;
      this.savedBatchTextareaCursor = 0;
    },
    
    // 解析日志文本
    parseLogText(text) {
      if (!text || !text.trim()) {
        return [];
      }
      
      const lines = text.split('\n').filter(line => line.trim());
      const parsed = [];
      
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        
        const logItem = {
          tag_code: null,
          log_text: '',
          status_success_text: '',
          status_failed_text: '',
          status_info_text: '',
          status_warning_text: '',
          components: [] // 添加组件信息数组
        };
        
        // 匹配格式：[标签] 日志文本 [图标] 状态反馈
        // 匹配标签 [XXX]
        const tagMatch = trimmed.match(/^\[([^\]]+)\]\s*(.+)$/);
        if (tagMatch) {
          logItem.tag_code = tagMatch[1].trim();
          const rest = tagMatch[2].trim();
          
          // 检查是否有状态反馈（✅、❌、ℹ️、⚠️）
          // 匹配格式：日志文本 图标 状态反馈（图标前后可能有空格）
          const statusMatch = rest.match(/^(.+?)\s*(✅|❌|ℹ️|⚠️)\s*(.+)$/);
          if (statusMatch) {
            logItem.log_text = statusMatch[1].trim();
            const icon = statusMatch[2];
            const statusText = statusMatch[3].trim();
            
            // 根据图标设置对应的状态文本
            if (icon === '✅') {
              logItem.status_success_text = statusText;
            } else if (icon === '❌') {
              logItem.status_failed_text = statusText;
            } else if (icon === 'ℹ️') {
              logItem.status_info_text = statusText;
            } else if (icon === '⚠️') {
              logItem.status_warning_text = statusText;
            }
          } else {
            logItem.log_text = rest;
          }
        } else {
          // 没有标签，直接作为日志文本
          logItem.log_text = trimmed;
        }
        
        // 检测组件占位符并关联组件信息
        if (logItem.log_text) {
          const componentPattern = /\{component:([\w_]+)(?::(\w+))?\}/g;
          let match;
          const foundComponents = new Set(); // 用于去重
          
          while ((match = componentPattern.exec(logItem.log_text)) !== null) {
            const componentCode = match[1];
            if (!foundComponents.has(componentCode)) {
              foundComponents.add(componentCode);
              const component = this.componentMap[componentCode];
              if (component) {
                logItem.components.push(component);
              }
            }
          }
        }
        
        if (logItem.log_text) {
          parsed.push(logItem);
        }
      }
      
      return parsed;
    },
    
    // 阶段选择器过滤
    filterStageOption(input, option) {
      const stage = this.allStagesList.find(s => s.id === option.value);
      if (!stage) return false;
      const searchText = input.toLowerCase();
      return stage.stage_code.toLowerCase().includes(searchText) ||
             stage.stage_name.toLowerCase().includes(searchText);
    },
    
    // 获取所有阶段（用于批量添加日志，只获取二级阶段）
    async fetchAllStages() {
      try {
        const response = await request.get('/diagnosis-display/stages', {
          params: {
            level: 2,
            pageSize: 1000
          }
        });
        
        if (response.code === 200) {
          this.allStagesList = (response.data.items || []).sort((a, b) => {
            // 先按 parent_id 排序，再按 stage_order 排序
            if (a.parent_id !== b.parent_id) {
              return a.parent_id - b.parent_id;
            }
            return a.stage_order - b.stage_order;
          });
        }
      } catch (error) {
        console.error('获取所有阶段失败:', error);
      }
    },
    
    // 批量添加日志提交
    async handleBatchAddLogsSubmit() {
      if (!this.batchAddLogsFormData.stage_id) {
        this.$message.warning('请选择阶段');
        return;
      }
      
      if (!this.batchAddLogsFormData.log_text || !this.batchAddLogsFormData.log_text.trim()) {
        this.$message.warning('请输入日志文本');
        return;
      }
      
      if (this.parsedLogs.length === 0) {
        this.$message.warning('未能解析出有效的日志，请检查格式');
        return;
      }
      
      try {
        // 获取当前阶段的日志数量，用于设置 display_order
        const currentLogs = this.stage2Logs[this.batchAddLogsFormData.stage_id] || [];
        let nextOrder = currentLogs.length > 0 
          ? Math.max(...currentLogs.map(log => log.display_order || 0)) + 1 
          : 0;
        
        // 批量创建日志项
        const promises = this.parsedLogs.map(logItem => {
          return request.post('/diagnosis-display/log-items', {
            stage_id: this.batchAddLogsFormData.stage_id,
            log_text: logItem.log_text,
            status_success_text: logItem.status_success_text || null,
            status_failed_text: logItem.status_failed_text || null,
            status_info_text: logItem.status_info_text || null,
            status_warning_text: logItem.status_warning_text || null,
            log_level: this.batchAddLogsFormData.log_level,
            tag_code: logItem.tag_code || null,
            display_order: nextOrder++,
            delay_ms: 0,
            wait_seconds: null,
            is_demo: 0
          });
        });
        
        await Promise.all(promises);
        
        this.$message.success(`成功添加 ${this.parsedLogs.length} 条日志`);
        this.batchAddLogsModalVisible = false;
        
        // 重新加载日志列表
        if (this.expandedStage2Id === this.batchAddLogsFormData.stage_id) {
          await this.fetchLogs(this.batchAddLogsFormData.stage_id);
        } else {
          // 如果当前阶段未展开，展开它
          const stage = this.level2Stages.find(s => s.id === this.batchAddLogsFormData.stage_id);
          if (stage) {
            this.expandedStage2Id = stage.id;
            this.selectedStage2 = stage;
            await this.fetchLogs(this.batchAddLogsFormData.stage_id);
          }
        }
      } catch (error) {
        this.$message.error('批量添加日志失败: ' + error.message);
      }
    },
    
    // 批量添加日志取消
    handleBatchAddLogsCancel() {
      this.batchAddLogsModalVisible = false;
      this.batchAddLogsFormData = {
        stage_id: null,
        log_text: '',
        log_level: 'info'
      };
      this.parsedLogs = [];
      this.selectedBatchComponent = undefined;
      this.savedBatchTextareaCursor = 0;
    }
  }
};
</script>

<style scoped>
.selected {
  background-color: #e6f7ff;
  border-left: 3px solid #1890ff;
}

.stage-row:hover {
  background-color: #f5f5f5;
}

.stage-row.expanded {
  border-color: #1890ff;
  background-color: #f0f8ff;
}

.console-log {
  background: #1e1e1e;
  border-radius: 4px;
  padding: 12px;
  font-family: 'Courier New', 'Consolas', monospace;
}

.console-item {
  display: flex;
  align-items: flex-start;
  padding: 4px 0;
  border-bottom: 1px solid #333;
}

.console-item:last-child {
  border-bottom: none;
}

.console-tag {
  color: #00ffff;
  margin-right: 8px;
}

.console-text {
  color: #d4d4d4;
}

.console-text.level-warning {
  color: #faad14;
}

.console-text.level-error {
  color: #ff4d4f;
}

.console-text.level-success {
  color: #52c41a;
}

.console-status {
  margin-top: 4px;
  padding-left: 12px;
}

.status-success {
  color: #52c41a;
  display: block;
}

.status-error {
  color: #ff4d4f;
  display: block;
}

.status-info {
  color: #1890ff;
  display: block;
}

.status-warning {
  color: #faad14;
  display: block;
}

.console-actions {
  display: flex;
  gap: 8px;
  margin-left: 12px;
  opacity: 0.5;
  transition: opacity 0.3s;
}

.console-item:hover .console-actions {
  opacity: 1;
}

.console-ellipsis {
  display: inline-block;
  margin-left: 4px;
  color: #d4d4d4;
}

.ellipsis-dot {
  display: inline-block;
  animation: ellipsis-blink 1.4s infinite;
}

.ellipsis-dot:nth-child(1) {
  animation-delay: 0s;
}

.ellipsis-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.ellipsis-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes ellipsis-blink {
  0%, 80%, 100% {
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
}
</style>
